import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Suspense, useEffect } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { InfoBanner } from "@whoosh/design-system/src/components/display/InfoBanner";
import { AlertIcon } from "@whoosh/design-system/src/components/icons_v2/general";

import { RateVerificationBannerQuery } from "./__generated__/RateVerificationBannerQuery.graphql";

type Props = {
  rateId: string;
  membershipTypeId: string;
};

export const RateVerificationBanner = ({ rateId, membershipTypeId }: Props) => {
  const [queryRef, loadQuery] = useQueryLoader<RateVerificationBannerQuery>(
    RateVerificationBannerQueryString
  );

  useEffect(() => {
    if (rateId && membershipTypeId) {
      loadQuery(
        { rateId, membershipTypeId },
        { fetchPolicy: "store-and-network" }
      );
    }
  }, [loadQuery, rateId, membershipTypeId]);

  return (
    <Suspense>
      {queryRef ? <RoundInfoBannerQueryRenderer queryRef={queryRef} /> : null}
    </Suspense>
  );
};

const RoundInfoBannerQueryRenderer = ({
  queryRef,
}: {
  queryRef: PreloadedQuery<RateVerificationBannerQuery>;
}) => {
  const data = usePreloadedQuery<RateVerificationBannerQuery>(
    RateVerificationBannerQueryString,
    queryRef
  );
  const rate = data.rateNode;
  const needsVerification = !rate?.appliesToMembershipType;
  return needsVerification ? (
    <InfoBanner
      leadingIcon={AlertIcon}
      colorTheme="WARNING"
      message={t`Rate verification required upon arrival`}
    />
  ) : null;
};

const RateVerificationBannerQueryString = graphql`
  query RateVerificationBannerQuery($rateId: ID!, $membershipTypeId: ID!) {
    rateNode: node(id: $rateId) {
      ... on Rate {
        appliesToMembershipType(membershipTypeId: $membershipTypeId)
      }
    }
  }
`;
